<template>
  <Layout>
    <div class="main">
      <PageHeader :title="computedTitle" :items="computedItems" />
      <div class="admin-menu-roles">
        <h2>Menu Role Management</h2>

        <div class="actions">
          <button @click="fetchMenus" class="btn">Reload Menus</button>
          <button
            @click="saveChanges"
            class="btn primary"
            :disabled="changes.length === 0 || loading"
          >
            {{ loading ? "Saving..." : "Save Changes" }}
          </button>
        </div>

        <menu-item-tree
          v-if="menus.length"
          :items="menus"
          :all-roles="allRoles"
          @update-roles="trackChange"
        />

        <p v-else class="no-data">No menu data available.</p>
      </div>
    </div>
  </Layout>
</template>

<script>
import menuApi from "@/api/menuApi";
import MenuItemTree from "@/components/MenuItemTree.vue";
import PageHeader from "@/components/page-header";
import Layout from "@/views/layouts/main";

export default {
  name: "AdminMenuRoles",
  components: { MenuItemTree, PageHeader, Layout },
  data() {
    return {
      menus: [],
      allRoles: ["USER", "OFFICER", "ADMIN"],
      changes: [],
      loading: false,
    };
  },
  created() {
    this.fetchMenus();
  },
  methods: {
    async fetchMenus() {
      try {
        this.loading = true;
        const res = await menuApi.getAllMenuItemsForAdmin();
        let rawMenu = res.data || [];

        // Only build tree if flat data (has parent_id)
        if (rawMenu.some((i) => i.parent_id !== undefined)) {
          rawMenu = this.buildTree(rawMenu);
        }

        // Deduplicate (same as sidebar)
        this.menus = this.deduplicateById(this.ensureRolesField(rawMenu));
      } catch (err) {
        console.error("Failed to load menus:", err);
        alert("Error loading menu data.");
      } finally {
        this.loading = false;
      }
    },

    buildTree(flatItems) {
      const map = {};
      const roots = [];

      flatItems.forEach((item) => {
        map[item.id] = { ...item, subItems: [] };
      });

      flatItems.forEach((item) => {
        if (item.parent_id && map[item.parent_id]) {
          map[item.parent_id].subItems.push(map[item.id]);
        } else if (!item.parent_id) {
          roots.push(map[item.id]);
        }
      });

      return roots;
    },

    deduplicateById(items) {
      const seen = new Set();
      const uniqueItems = [];

      for (const item of items) {
        if (!item.id || seen.has(item.id)) continue;
        seen.add(item.id);

        if (Array.isArray(item.subItems)) {
          item.subItems = this.deduplicateById(item.subItems);
        }

        uniqueItems.push(item);
      }

      return uniqueItems;
    },

    ensureRolesField(items) {
      return items.map((item) => ({
        ...item,
        requiredRoles: item.requiredRoles || [],
        subItems: item.subItems ? this.ensureRolesField(item.subItems) : [],
      }));
    },

    trackChange({ id, requiredRoles }) {
      const existing = this.changes.find((c) => c.id === id);
      if (existing) {
        existing.requiredRoles = requiredRoles;
      } else {
        this.changes.push({ id, requiredRoles });
      }
    },

    async saveChanges() {
      if (!this.changes.length) {
        alert("No changes to save.");
        return;
      }

      this.loading = true;
      try {
        await menuApi.updateMenuRolesBatch(this.changes);
        alert("Roles updated successfully!");
        this.changes = [];
        await this.fetchMenus();
      } catch (err) {
        console.error("Failed to update roles:", err);
        alert("Failed to save changes.");
      } finally {
        this.loading = false;
      }
    },
  },
  computed: {
    computedTitle() {
      return "Admin Menu Role Management";
    },
    computedItems() {
      return [{ text: "Admin" }, { text: "Menu Roles", active: true }];
    },
  },
};
</script>

<style scoped>
.admin-menu-roles {
  max-width: 900px;
  margin: 2rem auto;
  padding: 1.5rem;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
}

h2 {
  text-align: center;
  margin-bottom: 1.5rem;
}

.actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-bottom: 1rem;
}

.btn {
  padding: 0.5rem 1rem;
  border: none;
  background: #ccc;
  color: #333;
  cursor: pointer;
  border-radius: 4px;
}

.btn.primary {
  background: #1976d2;
  color: white;
}

.btn:disabled {
  background: #bbb;
  cursor: not-allowed;
}

.no-data {
  text-align: center;
  color: #777;
  margin-top: 2rem;
}
</style>
